<template>
    <v-row dense class="mb-4">
        <v-col cols="12" md="2">
            <span class="text-h6 text--secondary">
                {{ $t('key_facts') }}
            </span>
        </v-col>
        <v-col cols="12" md="8">
            <v-checkbox
                v-model="contract.applyDocumentsPersonally"
                hide-details
                :label="$t('submitted_by_tourists')"
                density="compact"
                class="pb-1"
                data-cy="submitted-by-tourists"
            />
            <v-row dense>
                <v-col cols="12" sm="6">
                    <div class="text-subtitle-1 font-weight-medium">{{ $t('country_of_destination') }}*</div>
                    <v-autocomplete
                        v-model="contract.countryId"
                        :items="useCountries"
                        item-title="title"
                        item-value="value"
                        closable-chips
                        hide-details
                        density="compact"
                        color="primary"
                        data-cy="country-id"
                        :rules="[requiredRule('country_of_destination')]"
                    />
                </v-col>
                <v-col cols="12" sm="6">
                    <div class="text-subtitle-1 font-weight-medium">{{ $t('applicants_country') }}</div>
                    <v-radio-group v-model="contract.allResidences" density="compact">
                        <v-radio
                            :label="$t('all_residences')"
                            :value="true"
                            color="primary"
                            data-cy="all-locations"
                            @click="contract.residences = []"
                        />
                        <v-radio
                            :label="$t('select_from_list')"
                            :value="false"
                            color="primary"
                            data-cy="select-from-list-locations"
                        />
                    </v-radio-group>
                    <CountryTable v-if="!contract.allResidences" v-model="contract.residences" />
                </v-col>
            </v-row>
        </v-col>
    </v-row>
    <v-row dense class="mb-4">
        <v-col cols="12" md="2">
            <span class="text-h6 text--secondary">
                {{ $t('documents') }}
            </span>
            <div class="px-0 font-italic text-subtitle-2">{{ $t('visa_document_note') }}</div>
        </v-col>
        <v-col cols="12" md="6">
            <UploadDocumentsForContract v-model="documents" />
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {useCountries} from '~/composables/form-helpers'
    import CountryTable from '~/components/products/information/CountryTable.vue'
    import UploadDocumentsForContract from '~/components/products/UploadDocumentsForContract.vue'

    const contract = defineModel<any>()
    const documents = ref([])

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (contract.value?.id) await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            const {images} = await app.$api.ownProductContractFiles.get(supplierStore.fullContract?.id)
            documents.value = images
        } catch (e) {
            documents.value = []
        }
    }

    const uploadFile = async (id?) => {
        const contractId = id ? id : supplierStore.fullContract?.id

        const toDelete = documents.value.filter(el => !!el.toDelete)
        if (toDelete?.length) await deleteFiles(toDelete)

        try {
            const toSaveFiles = documents.value.filter(({id}) => !id)
            await Promise.all(
                toSaveFiles.map(async el => {
                    const formData = new FormData()
                    formData.append('imageFile', el, el.name)
                    return await app.$api.ownProductContractFiles.post(contractId, formData)
                })
            )
            await loadData()
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        }
    }

    const deleteFiles = async toDelete => {
        for (let i = 0; i < toDelete.length; i++) {
            await app.$api.ownProductContractFiles.remove(supplierStore.fullContract?.id, toDelete[i]?.id)
        }
    }

    defineExpose({
        uploadFile,
    })
</script>
